import React from "react";
import { AlertCircle, CheckCircle } from "lucide-react";
import type { ImportResult, DuplicateInfo, DuplicateChoice } from "./types";

interface Props {
  importResult: ImportResult;
  duplicates: DuplicateInfo[];
  duplicateChoices: Record<string, DuplicateChoice>;
  onClose: () => void;
}

const StepResults: React.FC<Props> = ({ importResult, duplicates, duplicateChoices, onClose }) => {
  const skippedCount = duplicates.filter(
    (d) => (duplicateChoices[d.existingId] ?? "update") === "skip",
  ).length;

  return (
    <div>
      <h3 className="font-semibold mb-4">Import Results</h3>

      {(importResult.created > 0 || importResult.updated > 0) && (
        <div className="flex items-start gap-2 mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-800">
            {importResult.created > 0 && (
              <p className="font-medium">
                {importResult.created} product{importResult.created !== 1 ? "s" : ""} created.
              </p>
            )}
            {importResult.updated > 0 && (
              <p className="font-medium">
                {importResult.updated} existing product{importResult.updated !== 1 ? "s" : ""}{" "}
                updated.
              </p>
            )}
          </div>
        </div>
      )}

      {skippedCount > 0 && (
        <p className="text-xs text-gray-500 mb-3">
          {skippedCount} duplicate{skippedCount !== 1 ? "s" : ""} skipped as requested.
        </p>
      )}

      {importResult.errors.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={15} className="text-red-500 flex-shrink-0" />
            <h4 className="text-sm font-semibold text-red-600">
              {importResult.errors.length} row{importResult.errors.length !== 1 ? "s" : ""} had
              errors and were skipped:
            </h4>
          </div>

          <div className="max-h-56 overflow-y-auto border border-red-200 rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-red-50 sticky top-0">
                <tr>
                  <th className="p-2 text-left font-semibold text-red-800 border-b border-red-200">Row</th>
                  <th className="p-2 text-left font-semibold text-red-800 border-b border-red-200">Column</th>
                  <th className="p-2 text-left font-semibold text-red-800 border-b border-red-200">Value</th>
                  <th className="p-2 text-left font-semibold text-red-800 border-b border-red-200">Reason</th>
                </tr>
              </thead>
              <tbody>
                {importResult.errors.map((err, i) => (
                  <tr key={i} className="border-t border-red-100">
                    <td className="p-2 font-mono">{err.row || "—"}</td>
                    <td className="p-2 font-mono">{err.column}</td>
                    <td className="p-2 font-mono text-gray-700 max-w-[120px] truncate">
                      {err.value || <em className="text-gray-400">empty</em>}
                    </td>
                    <td className="p-2 text-red-700">{err.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {importResult.created === 0 &&
        importResult.updated === 0 &&
        importResult.errors.length === 0 && (
          <p className="text-sm text-gray-500 mb-4">No products were imported.</p>
        )}

      <button onClick={onClose} className="mt-2 w-full bg-blue-600 text-white p-3 rounded-md">
        Done
      </button>
    </div>
  );
};

export default StepResults;
